import React from "react";
import { Splide, SplideSlide, SplideTrack, type SplideProps } from "@splidejs/react-splide";
import "@splidejs/splide/dist/css/splide.min.css";
export type Shape = Splide;
export type Props = SplideProps;
type CompoundSlider = typeof SliderComponent & {
  Track: typeof TrackComponent;
  Slide: typeof SlideComponent;
};

// Two-finger trackpad pans arrive as wheel events with a dominant deltaX; Splide only handles
// pointer drags, so accumulated horizontal wheel deltas are translated into slide moves here.
const WHEEL_THRESHOLD = 30;
const WHEEL_COOLDOWN_MS = 350;
function useHorizontalWheel(splideRef: React.RefObject<Splide | null>) {
  React.useEffect(() => {
    const splide = splideRef.current?.splide;
    const root = splide?.root;
    if (!splide || !root) {
      return;
    }
    let accumulated = 0;
    let lastMove = 0;
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
        return;
      }
      const {
        index,
        length
      } = splide;
      const perPage = Number(splide.options.perPage) || 1;
      const atStart = index <= 0;
      const atEnd = index >= length - perPage;

      // Let the page handle the gesture (e.g. browser history swipe) once the slider can't move
      // any further in that direction.
      if (event.deltaX < 0 && atStart || event.deltaX > 0 && atEnd) {
        return;
      }
      event.preventDefault();
      const now = performance.now();
      if (now - lastMove < WHEEL_COOLDOWN_MS) {
        return;
      }
      accumulated += event.deltaX;
      if (Math.abs(accumulated) >= WHEEL_THRESHOLD) {
        splide.go(accumulated > 0 ? ">" : "<");
        accumulated = 0;
        lastMove = now;
      }
    };
    root.addEventListener("wheel", onWheel, {
      passive: false
    });
    return () => {
      root.removeEventListener("wheel", onWheel);
    };
  }, [splideRef]);
}
const TrackComponent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithRef<"div">>((props, ref) => {
  return <SplideTrack ref={ref} {...props} />;
});
const SlideComponent = React.forwardRef<HTMLLIElement, React.ComponentPropsWithRef<"li">>((props, ref) => {
  return <SplideSlide ref={ref} {...props} />;
});
const SliderComponent = React.forwardRef<Shape, SplideProps>((props, ref) => {
  const {
    children,
    options = {},
    ...rest
  } = props;
  const {
    arrows = false,
    pagination = false,
    ...restOptions
  } = options;
  const innerRef = React.useRef<Splide | null>(null);
  const setRef = React.useCallback((instance: Splide | null) => {
    innerRef.current = instance;
    if (typeof ref === "function") {
      ref(instance);
    } else if (ref) {
      ref.current = instance;
    }
  }, [ref]);
  useHorizontalWheel(innerRef);
  return <Splide ref={setRef} options={{
    arrows,
    pagination,
    ...restOptions
  }} {...rest}>
      {children}
    </Splide>;
});
const Slider = SliderComponent as CompoundSlider;
Slider.Track = TrackComponent;
Slider.Slide = SlideComponent;
export { Slider };